import { Box, Typography, useTheme } from "@mui/material";
import { tokens } from "../../theme";
import LineChart from "../../components/LineChart";
import StatBox from "../../components/StatBox";
import { useEffect, useState } from "react";
import { useMediaQuery } from "@mui/material";

// ✅ Icons
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";

const GoldPriceSystem = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [prices, setPrices] = useState({
    "24K": { current: 0, previous: 0 },
    "21K": { current: 0, previous: 0 },
    "23K": { current: 0, previous: 0 },
    "18K": { current: 0, previous: 0 },
    "22K": { current: 0, previous: 0 },

    // ✅ Silver (KG only)
    SilverKG: { current: 0, previous: 0 },
  });

  const [chartData, setChartData] = useState([]);
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState(0);
  const [countdown, setCountdown] = useState(15);

  const isMobileOrTablet = useMediaQuery("(max-width:1024px)");

  const fetchChartData = async (period) => {
    const periodUrls = {
      0: "https://api.daralsabaek.com/api/Chart/GetMetalPricesChartData?metalSymbol=1&periodType=0", // 10M
      1: "https://api.daralsabaek.com/api/Chart/GetMetalPricesChartData?metalSymbol=1&periodType=1", // 1h
      2: "https://api.daralsabaek.com/api/Chart/GetMetalPricesChartData?metalSymbol=1&periodType=2", // 3h
      3: "https://api.daralsabaek.com/api/Chart/GetMetalPricesChartData?metalSymbol=1&periodType=3", // 6h
      4: "https://api.daralsabaek.com/api/Chart/GetMetalPricesChartData?metalSymbol=1&periodType=4", // 12h
      5: "https://api.daralsabaek.com/api/Chart/GetMetalPricesChartData?metalSymbol=1&periodType=5", // 24h
    };

    try {
      const response = await fetch(periodUrls[period]);
      if (!response.ok) throw new Error("Failed to fetch chart data");

      const data = await response.json();
      if (!data.rangeOfXaxis || !data.rangeOfYaxis)
        throw new Error("Chart data is not available");

      const formattedData = [
        {
          id: "عيار 24",
          data: data.rangeOfXaxis.map((date, index) => {
            const newDate = new Date(date);
            newDate.setHours(newDate.getHours() + 3);

            return {
              x: newDate.toLocaleTimeString(),
              y: parseFloat(data.rangeOfYaxis[index].toFixed(3)),
            };
          }),
        },
      ];

      setChartData(formattedData);

      const allYValues = data.rangeOfYaxis.map((value) => parseFloat(value));
      setMinValue(Math.min(...allYValues));
      setMaxValue(Math.max(...allYValues));
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPrices = async () => {
    try {
      const response = await fetch(
        "https://api.daralsabaek.com/api/goldAndFundBalance/getMetalPrices",
      );
      if (!response.ok) throw new Error("Failed to fetch data");

      const data = await response.json();
      if (!data.isSuccess || !data.result)
        throw new Error("Data not available");

      const gold24K = data.result.find((item) => item.metalType === 1);
      const silver = data.result.find((item) => item.metalType === 2);

      if (!gold24K) throw new Error("Gold 24K price not found");
      if (!silver) throw new Error("Silver price not found");

      const new24KPrice = gold24K.buyPrice24;

      // ✅ silver.buyPrice24 = per gram => convert to KG
      const silverPerKg = silver.buyPrice24 * 1000;

      setPrices((prev) => ({
        "24K": { current: new24KPrice, previous: prev["24K"].current },
        "21K": {
          current: new24KPrice * (21 / 24),
          previous: prev["21K"].current,
        },
        "23K": {
          current: new24KPrice * (23 / 24),
          previous: prev["23K"].current,
        },
        "18K": {
          current: new24KPrice * (18 / 24),
          previous: prev["18K"].current,
        },
        "22K": {
          current: new24KPrice * (22 / 24),
          previous: prev["22K"].current,
        },

        // ✅ Silver KG only
        SilverKG: {
          current: silverPerKg,
          previous: prev.SilverKG.current,
        },
      }));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPrices();
    fetchChartData(selectedPeriod);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          fetchPrices();
          fetchChartData(selectedPeriod);
          return 15;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [selectedPeriod]);

  return (
    <Box m="20px" mt="-5px">
      <Box
        m="0px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
        >
          {isMobileOrTablet ? (
            <img src="../../assets/logo2.png" alt="Mobile Logo" width="195px" />
          ) : (
            <img width="195px" src="../../assets/logo2.png" alt="Grami" />
          )}
        </Box>
      </Box>

      <Box
        display="grid"
        gridTemplateColumns={isMobileOrTablet ? "1fr" : "repeat(12, 1fr)"}
        gridAutoRows="minmax(100px, auto)"
        gap="20px"
      >
        {/* Line Chart Section */}
        <Box
          gridColumn={isMobileOrTablet ? "span 1" : "span 8"}
          gridRow={isMobileOrTablet ? "span 1" : "span 4"}
          backgroundColor={colors.primary[400]}
        >
          <Box
            mt="25px"
            p="0 30px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
          >
            <Typography
              variant="h1"
              fontWeight="600"
              color={colors.grey[100]}
              sx={{ fontSize: isMobileOrTablet ? "18px" : "24px" }}
            >
              سيتم تحديث الاسعار خلال {countdown} حسب السعر العالمى
            </Typography>
          </Box>

          <Box height="400px" m="-20px 0 0 0">
            <LineChart
              data={chartData}
              minValue={minValue}
              maxValue={maxValue}
            />
          </Box>
        </Box>

        {/* Right Side Section */}
        <Box
          gridColumn={isMobileOrTablet ? "span 1" : "span 4"}
          gridRow={isMobileOrTablet ? "span 1" : "span 3"}
          display="flex"
          flexDirection="column"
          gap="20px"
        >
          {/* TradingView Widget */}
          <Box
            backgroundColor={colors.primary[400]}
            display="flex"
            alignItems="center"
            justifyContent="center"
            p="20px"
            borderRadius="10px"
          >
            <iframe
              src="https://www.tradingview-widget.com/embed-widget/single-quote/?locale=en#%7B%22frameElementId%22%3A%22scaled-frame%22%2C%22symbol%22%3A%22TVC%3AGOLD%22%2C%22width%22%3A370%2C%22colorTheme%22%3A%22dark%22%2C%22isTransparent%22%3Atrue%2C%22height%22%3A126%2C%22utm_source%22%3A%22daralsabaek.com%22%2C%22utm_medium%22%3A%22widget%22%2C%22utm_campaign%22%3A%22single-quote%22%2C%22page-uri%22%3A%22daralsabaek.com%2Ftrading-gold.html%22%7D"
              width="100%"
              height="100px"
              style={{ border: "none" }}
              title="TradingView Widget"
            />
          </Box>

          {/* StatBox Cards */}
          <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap="15px">
            {["24K", "22K", "21K", "18K", "SilverKG"].map((key) => {
              const label =
                key === "SilverKG" ? "الفضة بالكيلو" : `عيار ${key}`;

              // ✅ Gold icon vs Silver icon
              const icon =
                key === "SilverKG" ? (
                  <PaidOutlinedIcon
                    sx={{ color: colors.grey[100], fontSize: "32px" }}
                  />
                ) : (
                  <MonetizationOnOutlinedIcon
                    sx={{ color: colors.grey[100], fontSize: "32px" }}
                  />
                );

              return (
                <Box
                  key={key}
                  backgroundColor={colors.primary[400]}
                  display="grid"
                  alignItems="center"
                  justifyContent="center"
                  p="30px"
                  sx={{
                    borderRadius: "8px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    transition: "transform 0.3s ease",
                    "&:hover": { transform: "translateY(-5px)" },
                  }}
                >
                  <StatBox
                    title={prices[key].current.toFixed(3) + " KD"}
                    subtitle={label}
                    priceData={prices[key]}
                    icon={icon} // ✅ new icon
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default GoldPriceSystem;
